import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Target, MessageSquare } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { put } from '@/lib/api';

interface Goal {
  id: string;
  title: string;
  description?: string;
  status: string;
  progress: number;
}

interface Review {
  id: string;
  employee_id: string;
  employee_name: string;
  reviewer_id: string;
  reviewer_name: string;
  review_type: string;
  review_period: { start: string; end: string };
  status: string;
  due_date: string;
  overall_rating?: string;
  goals: Goal[];
  self_assessment?: string;
  manager_assessment?: string;
  strengths?: string[];
  areas_for_improvement?: string[];
  completed_at?: string;
  created_at: string;
}

const STATUS_FLOW = ['draft', 'self_review', 'manager_review', 'calibration', 'completed', 'acknowledged'];

const RATING_OPTIONS = ['exceptional', 'exceeds_expectations', 'meets_expectations', 'needs_improvement', 'unsatisfactory'];

export function ReviewDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: review, loading, refetch } = useApi<Review>(`reviews/${id}`);
  const [saving, setSaving] = useState(false);
  const [selfAssessment, setSelfAssessment] = useState('');
  const [managerAssessment, setManagerAssessment] = useState('');
  const [rating, setRating] = useState('');

  async function advanceStatus(status: string) {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = { status };
      if (status === 'manager_review' && selfAssessment) {
        payload.self_assessment = selfAssessment;
      }
      if (status === 'completed' || status === 'calibration') {
        if (managerAssessment) payload.manager_assessment = managerAssessment;
        if (rating) payload.overall_rating = rating;
      }
      await put(`reviews/${id}`, payload);
      refetch();
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-muted-foreground">Loading...</div>;
  if (!review) return <div className="text-muted-foreground">Review not found</div>;

  const currentIdx = STATUS_FLOW.indexOf(review.status);

  return (
    <div className="space-y-6 max-w-4xl">
      <button onClick={() => navigate('/reviews')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Back to Reviews
      </button>

      {/* Header */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground capitalize">
              {review.review_type.replace(/_/g, ' ')} Review
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Employee: <span className="text-foreground">{review.employee_name}</span> - Reviewer: <span className="text-foreground">{review.reviewer_name}</span>
            </p>
            <div className="flex items-center gap-3 mt-3">
              <StatusBadge status={review.status} />
              <span className="text-xs text-muted-foreground">Due: {review.due_date?.split('T')[0]}</span>
              {review.review_period && (
                <span className="text-xs text-muted-foreground">
                  Period: {review.review_period.start?.split('T')[0]} to {review.review_period.end?.split('T')[0]}
                </span>
              )}
            </div>
          </div>
          {review.overall_rating && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Rating</p>
              <p className="text-sm font-medium text-amber-500 capitalize">{review.overall_rating.replace(/_/g, ' ')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Status Pipeline */}
      <Card>
        <h3 className="text-sm font-semibold text-foreground mb-3">Review Workflow</h3>
        <div className="flex flex-wrap gap-2 items-center">
          {STATUS_FLOW.map((status, idx) => (
            <div key={status} className="flex items-center gap-2">
              <button
                onClick={() => void advanceStatus(status)}
                disabled={saving || status === review.status || idx < currentIdx}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${
                  status === review.status
                    ? 'bg-amber-500 text-foreground'
                    : idx < currentIdx
                    ? 'bg-emerald-500/10 text-emerald-400'
                    : 'bg-surface-3 text-muted-foreground hover:text-foreground hover:bg-surface-2'
                } disabled:opacity-50`}
              >
                {status.replace(/_/g, ' ')}
              </button>
              {idx < STATUS_FLOW.length - 1 && <span className="text-muted-foreground text-xs">&rarr;</span>}
            </div>
          ))}
        </div>
      </Card>

      {/* Goals */}
      {(review.goals ?? []).length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-amber-500" /> Goals
          </h3>
          <div className="space-y-3">
            {review.goals.map(goal => (
              <div key={goal.id} className="p-3 bg-surface-3 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-foreground">{goal.title}</p>
                  <StatusBadge status={goal.status} />
                </div>
                {goal.description && <p className="text-xs text-muted-foreground mb-2">{goal.description}</p>}
                <div className="w-full bg-surface-2 rounded-full h-1.5">
                  <div className="bg-amber-500 h-1.5 rounded-full transition-all" style={{ width: `${goal.progress}%` }} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{goal.progress}% complete</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Self Assessment */}
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-amber-500" /> Self Assessment
          </h3>
          {review.self_assessment ? (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{review.self_assessment}</p>
          ) : review.status === 'self_review' ? (
            <div className="space-y-3">
              <textarea
                value={selfAssessment}
                onChange={e => setSelfAssessment(e.target.value)}
                placeholder="Describe your accomplishments, challenges, and growth during this review period..."
                rows={5}
                className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-amber-500"
              />
              <button
                onClick={() => void advanceStatus('manager_review')}
                disabled={saving || !selfAssessment.trim()}
                className="px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50"
              >
                Submit Self Assessment
              </button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Not yet submitted</p>
          )}
        </Card>

        {/* Manager Assessment */}
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" /> Manager Assessment
          </h3>
          {review.manager_assessment ? (
            <div>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{review.manager_assessment}</p>
            </div>
          ) : review.status === 'manager_review' ? (
            <div className="space-y-3">
              <textarea
                value={managerAssessment}
                onChange={e => setManagerAssessment(e.target.value)}
                placeholder="Provide your assessment of this employee's performance..."
                rows={5}
                className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-amber-500"
              />
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Overall Rating</label>
                <select
                  value={rating}
                  onChange={e => setRating(e.target.value)}
                  className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-amber-500"
                >
                  <option value="">Select rating...</option>
                  {RATING_OPTIONS.map(r => (
                    <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => void advanceStatus('calibration')}
                disabled={saving || !managerAssessment.trim() || !rating}
                className="px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50"
              >
                Submit Manager Assessment
              </button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Not yet submitted</p>
          )}
        </Card>
      </div>

      {/* Strengths & Areas for Improvement */}
      {((review.strengths ?? []).length > 0 || (review.areas_for_improvement ?? []).length > 0) && (
        <div className="grid lg:grid-cols-2 gap-6">
          {(review.strengths ?? []).length > 0 && (
            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-3">Strengths</h3>
              <ul className="list-disc list-inside space-y-1">
                {review.strengths!.map((s, i) => (
                  <li key={i} className="text-sm text-muted-foreground">{s}</li>
                ))}
              </ul>
            </Card>
          )}
          {(review.areas_for_improvement ?? []).length > 0 && (
            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-3">Areas for Improvement</h3>
              <ul className="list-disc list-inside space-y-1">
                {review.areas_for_improvement!.map((a, i) => (
                  <li key={i} className="text-sm text-muted-foreground">{a}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
